import { useEffect, useRef, useState } from 'react'
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Plus,
  Settings,
  Stethoscope,
  Store,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useClients } from '../../features/clients/hooks'
import type { Client } from '../../features/clients/api'
import { useClientStore } from '../../stores/clientStore'
import { cn } from '../../lib/cn'

const INDUSTRY_META: Record<string, { icon: LucideIcon; label: string }> = {
  hospital: { icon: Building2, label: 'Hospital' },
  clinic: { icon: Stethoscope, label: 'Clinic' },
  sme: { icon: Store, label: 'SME' },
  other: { icon: Building2, label: 'Other' },
}

function ClientAvatar({
  client,
  className,
  fallbackClassName,
  iconSize,
}: {
  client: Client
  className: string
  fallbackClassName?: string
  iconSize: number
}) {
  const [imageFailed, setImageFailed] = useState(false)
  const Meta = INDUSTRY_META[client.industry] ?? INDUSTRY_META.other!

  useEffect(() => setImageFailed(false), [client.avatar_url])

  if (client.avatar_url && !imageFailed) {
    return (
      <img
        src={client.avatar_url}
        alt=""
        onError={() => setImageFailed(true)}
        className={cn('shrink-0 bg-black/[0.04] object-cover', client.current_user_role === 'viewer' && 'blur-md select-none', className)}
      />
    )
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        className,
        fallbackClassName,
      )}
    >
      <Meta.icon size={iconSize} />
    </span>
  )
}

export function ClientSwitcher() {
  const { data: clients, isPending } = useClients()
  const setCurrentClientId = useClientStore((s) => s.setCurrentClientId)
  const storeClientId = useClientStore((s) => s.currentClientId)
  const navigate = useNavigate()
  const { clientId: routeClientId } = useParams<{ clientId: string }>()
  const clientId = routeClientId ?? storeClientId ?? undefined
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (isPending) {
    return <div className="mono-tag text-black/55 px-2">Loading…</div>
  }
  if (!clients || clients.length === 0) {
    return <div className="mono-tag text-black/55 px-2">No clients</div>
  }

  const current = clients.find((client) => client.id === clientId) ?? clients[0]!

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-2 pl-3 pr-2.5 py-1.5 rounded-pill bg-white border border-black/[0.10] min-w-[14rem]',
          'text-body-sm font-540 text-black hover:border-black/25 transition-colors cursor-pointer',
          'focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black',
        )}
      >
        <ClientAvatar
          client={current}
          className="size-5 rounded-full"
          fallbackClassName="text-black/55"
          iconSize={14}
        />
        <span className={cn('flex-1 text-left truncate', current.current_user_role === 'viewer' && 'blur-md select-none')}>{current.name}</span>
        <ChevronDown
          size={14}
          className={cn('text-black/45 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full mt-1.5 z-30 w-[20rem] bg-white border border-black/[0.08] rounded-xl shadow-soft-lg overflow-hidden"
        >
          {/* Client Profile shortcut */}
          <button
            type="button"
            onClick={() => {
              navigate(`/clients/${clientId}/settings`)
              setOpen(false)
            }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-black/[0.05] hover:bg-brand-violet/[0.03] transition-colors group"
          >
            <div className="size-7 rounded-lg bg-brand-violet/10 flex items-center justify-center shrink-0">
              <Settings size={13} className="text-brand-violet" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[11px] font-540 text-black/70 group-hover:text-black">
                Client Profile
              </p>
              <p className={cn('text-[10px] text-black/40', current.current_user_role === 'viewer' && 'blur-md select-none')}>{current.name}</p>
            </div>
            <ChevronRight
              size={12}
              className="text-black/25 group-hover:text-brand-violet shrink-0"
            />
          </button>

          <div className="flex items-center justify-between px-4 pt-2.5 pb-2 border-b border-black/[0.05]">
            <span className="mono-tag text-black/45">Switch client</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1 text-[11px] font-540 text-brand-violet hover:text-brand-violet/80 transition-colors"
            >
              <Plus size={12} />
              เพิ่ม Client
            </button>
          </div>
          <div className="py-1 max-h-[280px] overflow-y-auto scrollbar-thin">
            {clients.map((client) => {
              const Meta = INDUSTRY_META[client.industry] ?? INDUSTRY_META.other!
              const isActive = client.id === clientId
              return (
                <button
                  key={client.id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => {
                    setCurrentClientId(client.id)
                    navigate(`/clients/${client.id}`)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    isActive ? 'bg-brand-violet/[0.05]' : 'hover:bg-black/[0.025]',
                  )}
                >
                  <ClientAvatar
                    client={client}
                    className="size-8 rounded-lg"
                    fallbackClassName={cn(
                      isActive
                        ? 'bg-brand-violet/15 text-brand-violet'
                        : 'bg-black/[0.04] text-black/60',
                    )}
                    iconSize={15}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        'font-540 text-body-sm truncate',
                        client.current_user_role === 'viewer' && 'blur-md select-none',
                        isActive ? 'text-black' : 'text-black/85',
                      )}
                    >
                      {client.name}
                    </p>
                    <p className="mono-tag text-black/50">{Meta.label}</p>
                  </div>
                  {isActive && <Check size={14} className="text-brand-violet shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
